/**
 * PresenceBadge — affiche les users actuellement en train de regarder le
 * même tender. Avatars en cercles avec initiales, tooltip avec email.
 *
 * Heartbeat tous les 30 s + poll de la liste tous les 20 s.
 */

import { useEffect, useState } from 'react';
import { api } from '../lib/api-client';

type Presence = {
  userId: string;
  email: string;
  pingAt: number;
};

const HEARTBEAT_MS = 30_000;
const POLL_MS = 20_000;

export function PresenceBadge({ tenderId }: { tenderId: string }) {
  const [users, setUsers] = useState<Presence[]>([]);

  // Heartbeat (envoie ma présence)
  useEffect(() => {
    let stopped = false;
    function ping() {
      api.post(`/api/tenders/${tenderId}/presence/heartbeat`).catch(() => {});
    }
    ping(); // immédiat
    const id = setInterval(() => {
      if (!stopped) ping();
    }, HEARTBEAT_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [tenderId]);

  // Poll list
  useEffect(() => {
    let stopped = false;
    function fetchList() {
      api
        .get<{ items: Presence[] }>(`/api/tenders/${tenderId}/presence`)
        .then((d) => {
          if (!stopped) setUsers(d.items ?? []);
        })
        .catch(() => {});
    }
    fetchList();
    const id = setInterval(fetchList, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [tenderId]);

  if (users.length === 0) return null;

  return (
    <div
      className="flex items-center gap-1"
      title={`${users.length} autre${users.length > 1 ? 's' : ''} en ligne`}
    >
      <span className="text-xs text-gray-500 mr-1">👥</span>
      <div className="flex -space-x-2">
        {users.slice(0, 5).map((u) => (
          <div
            key={u.userId}
            className="w-7 h-7 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold border-2 border-white dark:border-gray-900 ring-1 ring-green-400"
            title={u.email}
          >
            {initials(u.email)}
          </div>
        ))}
        {users.length > 5 && (
          <div className="w-7 h-7 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold border-2 border-white dark:border-gray-900">
            +{users.length - 5}
          </div>
        )}
      </div>
    </div>
  );
}

function initials(email: string): string {
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) {
    return ((parts[0]![0] ?? '') + (parts[1]![0] ?? '')).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}
